'use strict';
(() => {
  if (customElements.get('oluvie-cart-upsell')) return;
  customElements.define('oluvie-cart-upsell', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.owner = this.closest('oluvie-cart-drawer');
      this.core = window.OluvieCartUpsellCore;
      this.config = JSON.parse(this.querySelector('[data-or-upsell-config]').textContent);
      this.cards = this.querySelector('[data-or-upsell-cards]');
      this.status = this.querySelector('[data-or-upsell-status]');
      this.states = new Map(); this.uncertain = new Set(); this.cache = new Map(); this.generation = 0;
      this.abort = new AbortController();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-or-upsell-choose]');
        if (!button || this.busy || this.ownerBusy || this.reading) return;
        const state = this.states.get(button.dataset.orUpsellChoose);
        if (!state) return;
        state.expanded = !state.expanded; this.renderCards();
        const card = [...this.cards.children].find(node => node.dataset.handle === state.entry.handle);
        if (state.expanded) (card?.querySelector('select') || card?.querySelector('[data-or-upsell-add]'))?.focus({ preventScroll: true });
        else card?.querySelector('[data-or-upsell-choose]')?.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-or-upsell-option]');
        if (!input || this.busy || this.ownerBusy || this.reading) return;
        const state = this.states.get(input.closest('[data-handle]')?.dataset.handle);
        if (!state) return;
        const index = Number(input.dataset.orUpsellOption), value = input.value;
        state.choices[index] = state.product.options[index]?.values.map(String).includes(value) ? value : null;
        this.renderCards();
        const card = [...this.cards.children].find(node => node.dataset.handle === state.entry.handle);
        card?.querySelector(`[data-or-upsell-option="${index}"]`)?.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-or-upsell-form]');
        if (!form) return;
        event.preventDefault();
        void this.add(this.states.get(form.closest('[data-handle]')?.dataset.handle));
      }, { signal: this.abort.signal });
      if (this.pendingCart) void this.update(this.pendingCart);
      else if (this.owner?.cartTrusted && this.owner.cart) void this.update(this.owner.cart);
    }
    disconnectedCallback() {
      this.generation += 1; this.readController?.abort(); this.abort?.abort(); this.initialized = false;
    }
    setOwnerBusy(busy) {
      this.ownerBusy = busy;
      for (const control of this.cards?.querySelectorAll('button,select') || []) {
        control.disabled = Boolean(busy || this.busy || this.reading || control.dataset.unavailable === 'true' || this.uncertain.has(control.closest('[data-handle]')?.dataset.handle));
      }
    }
    async fetchData(url, text = false, externalSignal) {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store', headers: { Accept: text ? 'text/html' : 'application/json' }, signal: externalSignal ? AbortSignal.any([controller.signal, externalSignal]) : controller.signal });
        if (!response.ok) throw Error(this.config.strings.error);
        return text ? await response.text() : await response.json();
      } finally { clearTimeout(timeout); }
    }
    productUrl(handle) {
      if (!/^[a-z0-9][a-z0-9_-]{0,254}$/i.test(handle)) throw Error(this.config.strings.error);
      return new URL((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(handle), location.origin);
    }
    async readProduct(handle, signal, fresh = false) {
      const key = 'product:' + handle, cached = this.cache.get(key);
      if (!fresh && cached?.until > Date.now()) return cached.value;
      const url = this.productUrl(handle); url.pathname += '.js';
      const value = await this.fetchData(url, false, signal);
      if (!signal?.aborted) this.cache.set(key, { value, until: Date.now() + 45000 });
      return value;
    }
    async readContext(handle, signal, fresh = false) {
      const key = 'context:' + handle, cached = this.cache.get(key);
      if (!fresh && cached?.until > Date.now()) return cached.value;
      const url = this.productUrl(handle); url.searchParams.set('section_id', 'oluvie-cart-upsell-context');
      const html = await this.fetchData(url, true, signal);
      const data = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-or-upsell-context]');
      if (!data) throw Error(this.config.strings.error);
      const value = JSON.parse(data.textContent);
      if (!signal?.aborted) this.cache.set(key, { value, until: Date.now() + 45000 });
      return value;
    }
    async allowed(entry, cart) {
      if (!cart.items?.length || cart.items.length > 8) return false;
      try {
        const contexts = await Promise.all(cart.items.map(async item => {
          const context = await this.readContext(item.handle, undefined, true);
          if (Number(context.id) !== Number(item.product_id)) throw Error(this.config.strings.error);
          return context;
        }));
        const candidateContext = await this.readContext(entry.handle, undefined, true);
        const actual = this.core.classify(candidateContext, this.config.groups);
        if (Number(candidateContext.id) !== Number(entry.id) || actual?.gender !== entry.gender || actual?.category !== entry.category) return false;
        const pool = this.config.pool.filter(candidate => !this.uncertain.has(candidate.handle));
        return this.core.plan(contexts, pool, cart, this.config.groups, this.config.month).some(candidate => Number(candidate.id) === Number(entry.id));
      } catch (_) { return false; }
    }
    async update(cart) {
      this.pendingCart = cart;
      if (!this.initialized || this.busy || !this.core) return;
      this.readController?.abort(); this.readController = new AbortController();
      const signal = this.readController.signal, generation = ++this.generation;
      if (!cart.items?.length || cart.items.length > 8) { this.hidden = true; this.states.clear(); this.reading = false; return; }
      const signature = cart.items.map(item => Number(item.product_id)).sort((a, b) => a - b).join('|');
      if (this.cartSignature !== signature) this.states.clear();
      this.cartSignature = signature; this.reading = true;
      const inCart = new Set(cart.items.map(item => Number(item.product_id)));
      for (const [handle, state] of this.states) if (inCart.has(Number(state.entry.id))) this.states.delete(handle);
      this.renderCards();
      try {
        const contexts = await Promise.all(cart.items.map(async item => {
          const context = await this.readContext(item.handle, signal);
          if (Number(context.id) !== Number(item.product_id)) throw Error(this.config.strings.error);
          return context;
        }));
        const pool = this.config.pool.filter(entry => !this.uncertain.has(entry.handle));
        const entries = this.core.plan(contexts, pool, cart, this.config.groups, this.config.month);
        const resolved = await Promise.all(entries.map(async entry => {
          const [product, context] = await Promise.all([this.readProduct(entry.handle, signal), this.readContext(entry.handle, signal)]);
          const actual = this.core.classify(context, this.config.groups);
          if (!this.core.eligible(product, entry) || Number(context.id) !== Number(entry.id) || actual?.gender !== entry.gender || actual?.category !== entry.category) return null;
          const prior = this.states.get(entry.handle);
          return { entry, product, choices: prior?.choices || this.core.initialChoices(product), expanded: prior?.expanded || false };
        }));
        if (signal.aborted || generation !== this.generation || !this.isConnected) return;
        this.states = new Map(resolved.filter(Boolean).map(state => [state.entry.handle, state]));
        this.renderCards();
      } catch (_) {
        if (generation === this.generation && !signal.aborted) { this.states.clear(); this.renderCards(); }
      } finally {
        if (generation === this.generation) { this.reading = false; this.setOwnerBusy(this.ownerBusy); }
      }
    }
    imageUrl(value) {
      if (typeof value !== 'string' || !value) return '';
      try {
        const url = new URL(value, location.origin);
        if (url.protocol !== 'https:' || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) return '';
        url.searchParams.set('width', '256'); return url.href;
      } catch (_) { return ''; }
    }
    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined) node.textContent = text;
      return node;
    }
    renderCards() {
      if (!this.cards) return;
      const activeInside = this.contains(document.activeElement), fragment = document.createDocumentFragment();
      for (const state of [...this.states.values()].slice(0, 2)) {
        const { entry, product, choices } = state, selected = this.core.selectedVariant(product, choices);
        const card = this.element('article', 'or-cart-upsell__card'); card.dataset.handle = entry.handle;
        const link = this.element('a', 'or-cart-upsell__image'); link.href = this.productUrl(entry.handle); link.setAttribute('aria-label', product.title);
        const image = this.element('img'); image.width = 64; image.height = 80; image.loading = 'lazy'; image.alt = product.title;
        const imageSource = this.imageUrl(selected?.featured_image?.src || product.featured_image || entry.image);
        if (imageSource) image.src = imageSource; else image.hidden = true;
        link.append(image); card.append(link);
        const info = this.element('div', 'or-cart-upsell__info'), heading = this.element('h4');
        const title = this.element('a', '', product.title); title.href = this.productUrl(entry.handle); heading.append(title); info.append(heading);
        const prices = product.variants.filter(variant => variant.available && variant.price > 0).map(variant => variant.price);
        const price = selected?.price > 0 ? this.owner.money(selected.price) : this.owner.money(Math.min(...prices));
        info.append(this.element('p', 'or-cart-upsell__price', selected || new Set(prices).size === 1 ? price : this.config.strings.fromPrice.replace('__PRICE__', price)));
        const choose = this.element('button', 'or-cart-upsell__choose', this.config.strings.choose);
        choose.type = 'button'; choose.dataset.orUpsellChoose = entry.handle; choose.setAttribute('aria-expanded', String(state.expanded)); choose.setAttribute('aria-controls', 'OrUpsell-' + entry.id); info.append(choose); card.append(info);
        const form = this.element('form', 'or-cart-upsell__options'); form.dataset.orUpsellForm = ''; form.id = 'OrUpsell-' + entry.id; form.hidden = !state.expanded;
        if (!this.core.defaultOnly(product)) for (const [index, option] of product.options.entries()) {
          const label = this.element('label', 'or-cart-upsell__option'); label.append(this.element('span', '', option.name));
          const select = this.element('select'); select.dataset.orUpsellOption = String(index);
          const placeholder = this.element('option', '', this.config.strings.chooseOption); placeholder.value = ''; select.append(placeholder);
          for (const value of option.values) {
            const node = this.element('option', '', String(value)); node.value = String(value);
            node.disabled = !product.variants.some(variant => variant.available && variant.price > 0 && String(variant.options?.[index]) === String(value));
            select.append(node);
          }
          select.value = choices[index] || ''; label.append(select); form.append(label);
        }
        const add = this.element('button', 'or-cart-upsell__add', this.config.strings.add); add.type = 'submit'; add.dataset.orUpsellAdd = '';
        add.dataset.unavailable = String(!selected?.available || selected.price <= 0); form.append(add);
        if (choices.every(value => typeof value === 'string') && !selected?.available) form.append(this.element('p', 'or-cart-upsell__unavailable', this.config.strings.unavailable));
        card.append(form); fragment.append(card);
      }
      this.cards.replaceChildren(fragment); this.hidden = this.states.size === 0 && !this.criticalStatus;
      this.setOwnerBusy(this.ownerBusy);
      if (activeInside && this.owner?.dialog.open && !this.contains(document.activeElement)) (this.cards.querySelector('button:not(:disabled)') || this.owner.querySelector('[data-or-drawer-close]'))?.focus({ preventScroll: true });
    }
    async postAdd(variantId) {
      const url = new URL(this.config.addUrl, location.origin);
      if (url.origin !== location.origin) throw Error(this.config.strings.error);
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { method: 'POST', credentials: 'same-origin', mode: 'same-origin', redirect: 'error', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }), signal: controller.signal });
        if (!response.ok) { const error = Error(this.config.strings.error); error.confirmedRejection = response.status >= 400 && response.status < 500; throw error; }
      } finally { clearTimeout(timeout); }
    }
    async add(state) {
      if (!state || this.busy || this.reading || this.owner.busy || !this.owner.cartTrusted || this.uncertain.has(state.entry.handle)) return;
      const selected = this.core.selectedVariant(state.product, state.choices);
      if (!selected?.available || selected.price <= 0) return;
      this.readController?.abort(); this.generation += 1;
      this.busy = true; this.owner.mutating = true; this.owner.cartTrusted = false; this.owner.setBusy(true);
      this.criticalStatus = false; this.status.textContent = this.config.strings.loading;
      this.owner.setStatus(this.owner.config.strings.loading);
      try {
        const result = await this.core.performAdd(state, { readCart: () => this.owner.readCart(), allowed: (entry, cart) => this.allowed(entry, cart), readProduct: handle => this.readProduct(handle, undefined, true), postAdd: variant => this.postAdd(variant) });
        if (result.status === 'uncertain') this.uncertain.add(state.entry.handle);
        if (result.product && this.core.eligible(result.product, state.entry)) state.product = result.product;
        if (result.cart) { this.owner.cartTrusted = true; this.owner.render(result.cart); this.owner.publish(result.cart); }
        this.status.textContent = result.status === 'confirmed' ? this.config.strings.added : result.status === 'price_changed' ? this.config.strings.priceChanged : result.status === 'uncertain' ? this.config.strings.uncertain : result.status === 'unavailable' ? this.config.strings.unavailable : this.config.strings.error;
        this.criticalStatus = result.status !== 'confirmed';
        this.owner.setStatus(result.status === 'confirmed' ? this.owner.config.strings.added : this.status.textContent, this.criticalStatus);
        if (result.status === 'price_changed') this.renderCards();
        if (result.status === 'uncertain') this.owner.querySelector('[data-or-drawer-retry]').hidden = false;
      } catch (_) {
        this.criticalStatus = true; this.status.textContent = this.config.strings.error;
        this.owner.setStatus(this.status.textContent, true);
        this.owner.querySelector('[data-or-drawer-retry]').hidden = false;
      }
      finally {
        this.busy = false; this.owner.mutating = false; this.owner.setBusy(false);
        if (this.owner.refreshPending) { this.owner.refreshPending = false; void this.owner.refresh(this.owner.pendingMessage); }
        else if (this.pendingCart) void this.update(this.pendingCart);
      }
    }
  });
})();
